// src/admin/screens/kyc/modals/RequestResubmitModal.tsx
// Asks the borrower to upload new photos without fully rejecting the
// submission. Writes status = 'manual_review' (keeps the row in the
// reviewer queue) + review_notes = message. The borrower app reads
// review_notes and offers an "Re-upload documents" button without
// restarting KYC from scratch.
//
// Note on schema: the original plan referenced a `resubmit_requested_at`
// column and kept the submission status unchanged. Neither exists on
// the current schema — sub-project A never shipped that column — so we
// reuse `manual_review` + `review_notes` + `reviewed_at` for the
// timestamp. This keeps everything the borrower app needs in one place
// without introducing a schema drift under this plan.
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';

const schema = z.object({
  message: z.string().min(10, 'At least 10 characters'),
});
type Values = z.infer<typeof schema>;

interface Props {
  kyc: { id: string; user_id: string };
  onClose: (refresh: boolean) => void;
}

export default function RequestResubmitModal({ kyc, onClose }: Props) {
  const { admin } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({ resolver: zodResolver(schema) });

  const submit = async (v: Values) => {
    if (!admin) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from('kyc_submissions')
        .update({
          status: 'manual_review',
          reviewer_admin_id: admin.id,
          reviewed_at: new Date().toISOString(),
          review_notes: v.message,
        })
        .eq('id', kyc.id);
      if (error) throw error;
      await recordAudit({
        action: 'kyc.request_resubmit',
        target_type: 'kyc',
        target_id: kyc.id,
        payload: { borrower_id: kyc.user_id, message: v.message },
      });
      toast({
        kind: 'success',
        msg: 'Resubmission requested. Borrower will be notified.',
      });
      await qc.invalidateQueries({ queryKey: ['kyc', kyc.id] });
      await qc.invalidateQueries({ queryKey: ['kyc-queue'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => onClose(false)} title="Request resubmission">
      <form
        onSubmit={handleSubmit(submit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <p className="small">
          Use this when photos are unreadable or missing info. The borrower sees your message
          and can re-upload without restarting KYC.
        </p>
        <FormField
          label="Message to borrower"
          htmlFor="resubmit-message"
          error={errors.message?.message}
          required
        >
          <textarea
            id="resubmit-message"
            rows={4}
            {...register('message')}
            placeholder="e.g. The back of your NRC is blurry. Please retake in good light."
          />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Request resubmission
          </Button>
        </div>
      </form>
    </Modal>
  );
}
